<template>
  <article
    class="development-competency-card"
    role="button"
    tabindex="0"
    :aria-label="$t('general.view_competency_detail', { name: assignment.name })"
    @click="emitSelect"
    @keydown.enter.prevent="emitSelect"
    @keydown.space.prevent="emitSelect"
  >
    <div class="development-competency-card__heading">
      <span class="development-competency-card__icon" aria-hidden="true">
        <i class="uil-award"></i>
      </span>
      <div class="development-competency-card__title-wrap">
        <h3 class="development-competency-card__title">
          {{ assignment.name }}
        </h3>
      </div>
    </div>

    <p class="development-competency-card__description">
      {{ assignment.description || $t('general.no_description') }}
    </p>

    <dl class="development-competency-card__dates">
      <div>
        <dt>{{ $t('general.assigned_date') }}</dt>
        <dd>{{ formatDate(assignment.assigned_at) }}</dd>
      </div>
      <div>
        <dt>{{ $t('general.updated_date') }}</dt>
        <dd>{{ formatDate(assignment.updated_at) }}</dd>
      </div>
    </dl>

    <span
      class="development-competency-card__detail"
      aria-hidden="true"
    >
      {{ $t('general.view_detail') }}
      <i class="uil-arrow-right" aria-hidden="true"></i>
    </span>
  </article>
</template>

<script>
import moment from "moment";

export default {
  name: "DevelopmentCompetencyCard",
  props: {
    assignment: {
      type: Object,
      required: true,
    },
  },
  methods: {
    emitSelect() {
      this.$emit("select", this.assignment);
    },
    formatDate(value) {
      if (!value) return "-";
      const date = moment(value);
      return date.isValid() ? date.format("DD.MM.YYYY") : "-";
    },
  },
};
</script>

<style lang="scss" scoped>
.development-competency-card {
  --competency-accent: #3568d4;
  --competency-accent-soft: #edf3ff;
  position: relative;
  display: flex;
  width: 100%;
  min-width: 0;
  min-height: 270px;
  flex-direction: column;
  padding: 22px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e6eaf0;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(37, 48, 68, 0.06);
  cursor: pointer;
  box-sizing: border-box;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover,
  &:focus-visible {
    border-color: var(--competency-accent);
    box-shadow: 0 10px 28px rgba(37, 48, 68, 0.12);
    outline: none;
    transform: translateY(-2px);
  }
}

.development-competency-card__heading {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.development-competency-card__icon {
  display: inline-flex;
  width: 52px;
  height: 52px;
  flex: 0 0 52px;
  align-items: center;
  justify-content: center;
  color: var(--competency-accent);
  background: var(--competency-accent-soft);
  border-radius: 50%;

  i {
    font-size: 25px;
  }
}

.development-competency-card__title-wrap {
  min-width: 0;
}

.development-competency-card__title {
  overflow-wrap: anywhere;
  margin: 2px 0 0;
  color: #222b3a;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.35;
}

.development-competency-card__description {
  display: -webkit-box;
  min-height: 60px;
  margin: 18px 0;
  overflow: hidden;
  color: #687386;
  font-size: 13px;
  line-height: 1.55;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}

.development-competency-card__dates {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  padding-top: 14px;
  margin: auto 0 16px;
  border-top: 1px solid #eef0f4;

  div {
    min-width: 0;
  }

  dt {
    margin-bottom: 3px;
    color: #8b94a3;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
  }

  dd {
    margin: 0;
    color: #444f60;
    font-size: 12px;
    font-weight: 600;
  }
}

.development-competency-card__detail {
  align-self: flex-end;
  padding: 3px 0;
  color: var(--competency-accent);
  background: transparent;
  border: 0;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  i {
    margin-left: 3px;
  }

}

@media (max-width: 575px) {
  .development-competency-card {
    min-height: 250px;
    padding: 16px;
  }

  .development-competency-card__heading {
    gap: 10px;
  }

  .development-competency-card__icon {
    width: 44px;
    height: 44px;
    flex-basis: 44px;
  }

  .development-competency-card__icon i {
    font-size: 21px;
  }

  .development-competency-card__title {
    font-size: 15px;
  }

  .development-competency-card__description {
    min-height: 0;
    margin: 14px 0;
    font-size: 12px;
  }

  .development-competency-card__dates {
    gap: 8px;
  }

  .development-competency-card__dates dd {
    font-size: 11px;
  }
}

@media (max-width: 639px) {
  .development-competency-card {
    min-height: 0;
    padding: 16px;
    border-radius: 12px;
  }

  .development-competency-card__title {
    font-size: 16px;
    line-height: 1.4;
  }

  .development-competency-card__description {
    min-height: 0;
    margin: 12px 0;
    font-size: 14px;
    line-height: 1.5;
    -webkit-line-clamp: 3;
  }

  .development-competency-card__dates {
    gap: 8px;
    padding-top: 12px;
    margin-bottom: 12px;
  }

  .development-competency-card__dates dt {
    font-size: 11px;
  }

  .development-competency-card__dates dd {
    font-size: 12px;
  }

  .development-competency-card__detail {
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    padding: 8px 0;
    font-size: 14px;
  }
}
</style>
